import * as React from "react"
import { cn } from "../../lib/utils"
import { ChevronUp, ChevronDown, Filter, X } from "lucide-react"
import { Button } from "./button"
import { Input } from "./input"

const Table = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement>
>(({ className, ...props }, ref) => (
  <div className="relative w-full overflow-auto">
    <table
      ref={ref}
      className={cn("w-full caption-bottom text-sm", className)}
      {...props}
    />
  </div>
))
Table.displayName = "Table"

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead ref={ref} className={cn("[&_tr]:border-b", className)} {...props} />
))
TableHeader.displayName = "TableHeader"

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn("[&_tr:last-child]:border-0", className)}
    {...props}
  />
))
TableBody.displayName = "TableBody"

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn("border-t bg-muted/50 font-medium [&>tr]:last:border-b-0", className)}
    {...props}
  />
))
TableFooter.displayName = "TableFooter"

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      "border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted",
      className
    )}
    {...props}
  />
))
TableRow.displayName = "TableRow"

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement> & {
    sortable?: boolean;
    sorted?: 'asc' | 'desc' | null;
    onSort?: () => void;
    filterable?: boolean;
    filterValue?: string;
    onFilterChange?: (value: string) => void;
    onClearFilter?: () => void;
    columnName?: string;
  }
>(({ className, children, sortable, sorted, onSort, filterable, filterValue, onFilterChange, onClearFilter, columnName, ...props }, ref) => {
  const [showFilter, setShowFilter] = React.useState(false);
  const [inputHasFocus, setInputHasFocus] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  
  // Show filter if there's a value OR if input has focus
  React.useEffect(() => {
    if (filterValue && filterValue.trim()) {
      setShowFilter(true);
    } else if (inputHasFocus) {
      // Keep visible if input has focus, even when empty
      setShowFilter(true);
    } else {
      // Hide only when no value and no focus
      setShowFilter(false);
    }
  }, [filterValue, inputHasFocus]);
  
  // Additional effect to maintain visibility after focus restoration
  React.useLayoutEffect(() => {
    const activeElement = document.activeElement;
    const thisInputHasFocus = activeElement && 
      activeElement.getAttribute('data-column') === columnName;
    
    if (thisInputHasFocus && !inputHasFocus) {
      // Update focus state if DOM shows this input is focused but state doesn't reflect it
      setInputHasFocus(true);
    }
  });
  
  return (
    <th
      ref={ref}
      className={cn(
        "h-12 px-4 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0",
        className
      )}
      {...props}
    >
      <div className="flex flex-col">
        <div className="flex items-center space-x-2">
          <span className="flex-1">{children}</span>
          {sortable && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onSort}
              className="h-6 w-6 p-0"
            >
              {sorted === 'asc' ? (
                <ChevronUp className="h-4 w-4" />
              ) : sorted === 'desc' ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <div className="flex flex-col h-4 w-4">
                  <ChevronUp className="h-2 w-4 opacity-50" />
                  <ChevronDown className="h-2 w-4 opacity-50" />
                </div>
              )}
            </Button>
          )}
          {filterable && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowFilter(!showFilter)}
              className="h-6 w-6 p-0"
            >
              <Filter className="h-3 w-3" />
            </Button>
          )}
        </div>
        {filterable && showFilter && (
          <div className="mt-2 relative">
            <Input
              ref={inputRef}
              placeholder="Filter..."
              value={filterValue || ''}
              onChange={(e) => onFilterChange?.(e.target.value)}
              onFocus={() => {
                setInputHasFocus(true);
              }}
              onBlur={() => {
                setInputHasFocus(false);
              }}
              className="h-8 text-xs pr-8"
              data-column={columnName}
            />
            {(filterValue && filterValue.trim()) || inputHasFocus ? (
              <Button
                variant="ghost"
                size="sm"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  
                  // Hide the filter and remove focus
                  setShowFilter(false);
                  setInputHasFocus(false);
                  
                  // Blur the input
                  if (inputRef.current) {
                    inputRef.current.blur();
                  }
                  
                  // Call the clear filter function which will handle both state update and API call
                  if (onClearFilter) {
                    onClearFilter();
                  } else {
                    // Fallback to just clearing the value
                    onFilterChange?.('');
                  }
                }}
                className="absolute right-1 top-0 h-8 w-6 p-0 hover:bg-gray-100"
                title={filterValue && filterValue.trim() ? "Vymazat filtr" : "Zrušit filtrování"}
              >
                <X className="h-3 w-3" />
              </Button>
            ) : null}
          </div>
        )}
      </div>
    </th>
  )
})
TableHead.displayName = "TableHead"

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn("p-4 align-middle [&:has([role=checkbox])]:pr-0", className)}
    {...props}
  />
))
TableCell.displayName = "TableCell"

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn("mt-4 text-sm text-muted-foreground", className)}
    {...props}
  />
))
TableCaption.displayName = "TableCaption"

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
}
